import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, ArrowRight, ExternalLink } from 'lucide-react';

// ─── The core comparison matrix ────────────────────────────────────────
const ROWS = [
  {
    question: 'What does the system verify?',
    experian: 'Identity: who the agent is, and which verified human it represents.',
    kojiru: 'Commitment: the operator has posted a public pledge next to specific promises.',
    winner: 'both',
  },
  {
    question: 'What backs the verification?',
    experian: 'A cryptographic Trust Token, refreshed per transaction, derived from human-to-agent binding.',
    kojiru: 'A public USDC pledge on Base, sized to the agent\u2019s tier, visible to every counterparty.',
    winner: 'both',
  },
  {
    question: 'Where does the record live?',
    experian: 'The credit bureau\u2019s Agent Registry (private, updated by the credit bureau).',
    kojiru: 'The KYPA Directory (public, on-chain, source-verified on BaseScan).',
    winner: 'kojiru',
  },
  {
    question: 'When a verified agent still delivers the wrong outcome, what happens?',
    experian: 'The agent\u2019s dynamic trust score can be revised downward for future transactions.',
    kojiru: 'The breach is recorded publicly against the operator\u2019s pledge, a public commitment signal anyone can check before the next transaction.',
    winner: 'kojiru',
  },
  {
    question: 'Does the system promise to repay a harmed party?',
    experian: 'No. Trust signaling only.',
    kojiru: 'No. Kojiru is not insurance and assumes no liability. The pledge is a public commitment signal.',
    winner: 'none',
  },
  {
    question: 'Distribution today',
    experian: 'Edge enforcement via major CDN providers. Integrations with a card-network agent protocol and an agent payments startup KYAPay.',
    kojiru: 'Direct integration with x402, cloud-hyperscaler agent payments, and any protocol that accepts an on-chain attestation.',
    winner: 'both',
  },
  {
    question: 'Pricing',
    experian: 'Not publicly disclosed (as of August 25, 2026).',
    kojiru: '0.05% per side, on-chain, deducted by the settlement contract. Transparent.',
    winner: 'kojiru',
  },
  {
    question: 'Ownership of the trust record',
    experian: 'Owned by the credit bureau. Access via API contract.',
    kojiru: 'Owned by no one. Published to a permissionless registry on Base.',
    winner: 'kojiru',
  },
];

const WinnerCell = ({ text, isWinner }) => (
  <div className={`text-sm leading-relaxed ${isWinner ? 'text-white' : 'text-slate-400'}`}>
    {text}
  </div>
);

export default function VsExperianAgentTrust() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Kojiru vs. a Credit Bureau Agent Trust Product · Two Ways to Trust an Agent</title>
        <meta
          name="description"
          content="An honest side-by-side. A major credit bureau verifies who an agent is. Kojiru verifies what happens when the verified agent is wrong."
        />
        <meta property="og:title" content="Kojiru vs. a Credit Bureau Agent Trust Product" />
        <meta property="og:description" content="Two ways to trust an agent. Both are legitimate. They answer different questions." />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-6">COMPARISON &middot; PUBLISHED 25 AUGUST 2026</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              Two ways to trust an agent.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl">
              A major credit bureau&rsquo;s agent trust product and Kojiru KYPA are often placed in the same category. They shouldn&rsquo;t be.
              They answer different questions, and together they answer more of the question than either does alone.
              Here is the honest side-by-side.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The one-line difference */}
      <section className="py-14 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="bg-[#0f0f16] border border-blue-500/30 rounded-xl p-8">
            <div className="text-[10px] tracking-[2px] text-blue-300 mb-3">CREDIT BUREAU AGENT TRUST</div>
            <p className="text-2xl font-bold leading-snug">
              Verifies <span className="text-blue-300">who</span> an agent is, and which verified human it represents.
            </p>
          </div>
          <div className="bg-[#0f0f16] border border-rose-500/30 rounded-xl p-8">
            <div className="text-[10px] tracking-[2px] text-rose-300 mb-3">KOJIRU KYPA</div>
            <p className="text-2xl font-bold leading-snug">
              Verifies <span className="text-rose-300">what happens</span> when the verified agent is wrong.
            </p>
          </div>
        </div>
      </section>

      {/* The comparison matrix */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-4">SIDE-BY-SIDE</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-10 leading-tight">
            Eight questions. Two answers each.
          </h2>

          <div className="border border-white/10 rounded-xl overflow-hidden bg-[#0f0f16]">
            <div className="grid grid-cols-[1fr,1fr,1fr] gap-x-4 sm:gap-x-6 px-4 sm:px-6 py-4 border-b border-white/10 bg-white/5 text-[10px] font-bold tracking-[1.5px] text-slate-400">
              <div>QUESTION</div>
              <div>CREDIT BUREAU AGENT TRUST</div>
              <div>KOJIRU KYPA</div>
            </div>
            {ROWS.map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="grid grid-cols-[1fr,1fr,1fr] gap-x-4 sm:gap-x-6 px-4 sm:px-6 py-5 items-start border-b border-white/5 last:border-b-0"
                data-testid={`vs-row-${i}`}
              >
                <div className="text-sm font-semibold text-white">{row.question}</div>
                <WinnerCell text={row.experian} isWinner={row.winner === 'experian' || row.winner === 'both'} />
                <WinnerCell text={row.kojiru} isWinner={row.winner === 'kojiru' || row.winner === 'both'} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Two paths, complementary */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            The honest read: use both.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            An enterprise buyer preparing for agentic commerce needs an answer to two distinct questions before any
            volume arrives.
          </p>
          <p className="text-slate-300 leading-relaxed mb-6">
            <span className="text-white font-semibold">The first question is identity.</span> When an agent arrives
            to buy from your service, do you know it is an agent? Can you tell whose authority it carries? That is
            what the credit bureau Agent Trust is built for, and what it does well. It uses a cryptographic Trust Token,
            refreshed per transaction, delivered at the network edge through major CDN providers.
          </p>
          <p className="text-slate-300 leading-relaxed mb-6">
            <span className="text-white font-semibold">The second question is accountability.</span> When the agent
            passes the identity check, uses its permitted spending limit, and still delivers the wrong outcome, who
            stands behind it? Kojiru KYPA makes that visible. It uses a public USDC pledge on Base, sized to the
            agent&rsquo;s tier, as a public commitment signal. It is not insurance and not a promise of repayment.
          </p>
          <p className="text-slate-300 leading-relaxed">
            Neither system tries to answer the other&rsquo;s question. A serious agentic-commerce stack in 2027
            will use both, together with a payments rail (the USDC issuer, x402, a card-issuing startup) and a fraud engine (a major card network, a major card network).
            Four layers. Four questions. Four different specialists.
          </p>
        </div>
      </section>

      {/* Where they meet */}
      <section className="py-16 px-6 border-b border-white/5 bg-gradient-to-b from-transparent to-blue-950/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">
            Where the two systems interoperate
          </h2>
          <ul className="space-y-4">
            <li className="flex gap-4 items-start">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">An agent can carry both signals in the same request.</span>{' '}
                The credit bureau&rsquo;s Trust Token attests the human principal. A KYPA ID attests the public pledge. Together
                they answer both questions in a single handshake.
              </p>
            </li>
            <li className="flex gap-4 items-start">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">Trust score and pledge tier are complementary.</span>{' '}
                The credit bureau&rsquo;s dynamic trust score is a probability signal. Kojiru&rsquo;s pledge tier is an
                economic commitment. A high trust score with no pledge is a promise without collateral. A high
                pledge with no trust score is collateral without a principal.
              </p>
            </li>
            <li className="flex gap-4 items-start">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">Both are open to inspection by regulators and auditors.</span>{' '}
                The credit bureau offers a controlled API. Kojiru offers a permissionless on-chain registry. Different access
                models, same evidence class.
              </p>
            </li>
          </ul>
        </div>
      </section>

      {/* Where they diverge */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">
            Where they diverge
          </h2>
          <ul className="space-y-4">
            <li className="flex gap-4 items-start">
              <the USDC issuer className="w-5 h-5 text-rose-400 mt-1 flex-shrink-0" />
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">Confidence versus collateral.</span> The credit bureau&rsquo;s 2026
                fraud report cites <em>91% of businesses confident in emerging Know Your Agent capabilities.</em>{' '}
                That is the credit bureau&rsquo;s own survey figure for a product the credit bureau sells. It is a signal about
                confidence, not a mechanism for consequence. Kojiru requires actual USDC before it will call an
                agent verified. If the agent turns out to be wrong, the USDC is what moves.
              </p>
            </li>
            <li className="flex gap-4 items-start">
              <the USDC issuer className="w-5 h-5 text-rose-400 mt-1 flex-shrink-0" />
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">Private registry versus public ledger.</span> The credit bureau
                controls its Agent Registry. Kojiru&rsquo;s KYPA Directory is on-chain and permissionless. Both are
                defensible design choices; the trade-off is corporate accountability versus radical transparency.
              </p>
            </li>
            <li className="flex gap-4 items-start">
              <the USDC issuer className="w-5 h-5 text-rose-400 mt-1 flex-shrink-0" />
              <p className="text-slate-300 leading-relaxed">
                <span className="text-white font-semibold">Verify vs. record.</span> This is the fundamental
                difference. The credit bureau&rsquo;s system stops at the transaction boundary: a signal that helps
                the counterparty decide whether to proceed. Kojiru&rsquo;s system extends past the transaction
                boundary: a public breach record next to the operator&rsquo;s pledge when the signal turns out to be wrong. Kojiru does not pay claims.
              </p>
            </li>
          </ul>
        </div>
      </section>

      {/* Close */}
      <section className="py-16 px-6 border-t border-white/5 bg-gradient-to-b from-transparent to-rose-950/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            Trust is not one thing.
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            Every serious agentic-commerce stack in the next 24 months will need at least three of these four:
            a rail, a fraud engine, an identity signal, and a recourse mechanism. Kojiru is the fourth. If you are
            already thinking about the first three, we would like to help you think about the fourth.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/kypa">
              <Button size="lg" className="bg-white text-black hover:bg-slate-100" data-testid="learn-kypa-btn">
                Learn how KYPA works
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="https://www.experian.com/business/products/agent-trust" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10" data-testid="visit-experian-btn">
                Visit the credit bureau Agent Trust
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
          <p className="text-xs text-slate-600 mt-8">
            Every claim on this page has been checked against the primary sources on the date given. Sources: the credit bureau
            press release (30 April 2026); the credit bureau 11th annual <em>Identity and Fraud Report</em> (19 August 2026);
            A major CDN provider &times; the credit bureau technical deep dive (2026); KojiruACSHook on BaseScan.
            If you spot an error, please tell us: wayne@kojiru.com.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
